/* ==========================================================================
   flux-afspraak — de datum- en tijdkiezer van het afspraakformulier.

   De datumkiezer is het <input type="date"> van de browser zelf. Dit element
   zet alleen de grenzen eromheen:

   - niet in het verleden en niet verder dan het ingestelde aantal dagen;
   - gesloten dagen worden geweigerd, met een regel eronder waarom;
   - de tijdvakken volgen de gekozen dag (zaterdag heeft een eigen rij);
   - het postcodeveld verschijnt alleen bij een afspraak op locatie.

   Zonder JavaScript blijft het een gewoon formulier: je kunt een datum kiezen
   en het veld voor het tijdvak is leeg. De aanvraag komt dan nog steeds aan;
   Berat bevestigt hem toch handmatig.
   ========================================================================== */

const DAGEN = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];

class FluxAfspraak extends HTMLElement {
  connectedCallback() {
    this.datum = this.querySelector('[data-datum]');
    this.tijd = this.querySelector('[data-tijd]');
    this.melding = this.querySelector('[data-datum-melding]');
    this.postcode = this.querySelector('[data-postcode]');
    if (!this.datum || !this.tijd) return;

    this.gesloten = (this.dataset.gesloten || '')
      .split(',')
      .map((d) => d.trim().toLowerCase())
      .filter(Boolean);
    this.vakken = this.#lijst(this.dataset.tijdvakken);
    this.vakkenZaterdag = this.#lijst(this.dataset.tijdvakkenZaterdag);

    this.#grenzen();
    this.datum.addEventListener('change', () => this.#kies());
    this.datum.addEventListener('input', () => this.#kies());

    for (const soort of this.querySelectorAll('[data-soort]')) {
      soort.addEventListener('change', () => this.#locatie());
    }
    this.#locatie();
  }

  #lijst(waarde) {
    return (waarde || '')
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  }

  /* Vanaf morgen, tot het ingestelde aantal dagen vooruit. Vandaag niet: een
     monteur of een showroomafspraak regel je niet binnen een paar uur. */
  #grenzen() {
    const vooruit = Number(this.dataset.dagenVooruit) || 56;
    const morgen = new Date();
    morgen.setDate(morgen.getDate() + 1);
    const laatste = new Date();
    laatste.setDate(laatste.getDate() + vooruit);

    this.datum.min = this.#isoDatum(morgen);
    this.datum.max = this.#isoDatum(laatste);
  }

  #isoDatum(d) {
    // Niet toISOString: die rekent naar UTC en schuift in de zomer een dag op.
    const maand = String(d.getMonth() + 1).padStart(2, '0');
    const dag = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${maand}-${dag}`;
  }

  #kies() {
    const waarde = this.datum.value;
    if (!waarde) return this.#vul([]);

    const dag = new Date(`${waarde}T12:00:00`);
    const kort = DAGEN[dag.getDay()];

    if (this.gesloten.includes(kort)) {
      this.datum.setCustomValidity(this.#tekst('gesloten'));
      this.#toonMelding(this.#tekst('gesloten'));
      return this.#vul([]);
    }

    this.datum.setCustomValidity('');
    this.#toonMelding('');
    this.#vul(kort === 'za' && this.vakkenZaterdag.length ? this.vakkenZaterdag : this.vakken);
  }

  #vul(vakken) {
    const gekozen = this.tijd.value;
    const leeg = this.tijd.querySelector('option[value=""]');
    this.tijd.replaceChildren(leeg || new Option(this.#tekst('kies'), ''));
    for (const vak of vakken) this.tijd.append(new Option(vak, vak));
    if (vakken.includes(gekozen)) this.tijd.value = gekozen;
  }

  #locatie() {
    if (!this.postcode) return;
    const gekozen = this.querySelector('[data-soort]:checked');
    const nodig = gekozen?.dataset.locatie === 'true';
    this.postcode.hidden = !nodig;
    const veld = this.postcode.querySelector('input');
    if (veld) veld.required = nodig;
  }

  #toonMelding(tekst) {
    if (!this.melding) return;
    this.melding.textContent = tekst;
    this.melding.hidden = !tekst;
  }

  /* De teksten staan als data-attribuut op het element, zodat ze vertaalbaar
     blijven via de locale-bestanden en hier geen Nederlands in de code staat. */
  #tekst(sleutel) {
    return sleutel === 'gesloten' ? this.dataset.geslotenTekst || '' : this.dataset.kies || '';
  }
}

if (!customElements.get('flux-afspraak')) {
  customElements.define('flux-afspraak', FluxAfspraak);
}
